import { CSSProperties, KeyboardEvent, MouseEvent, useCallback, useEffect, useMemo, useRef } from "react";

import { Shelf } from "capitalrx-components-2.0/lib/layout";
import { Semibold } from "capitalrx-components-2.0/lib/typography";
import classNames from "classnames";

import { Color } from "..";
import { Icon } from "../Icon";
import { SortDirection } from "../Sortable";
import { BaseColumnProps } from "./BaseColumnProps";
import { HeaderWrapper } from "./HeaderWrapper";
import { useTableStyles } from "./TableStylesContext";

/**
 * The `TableColumnHeader` component properties.
 */
export interface TableColumnHeaderProps extends BaseColumnProps {
  /**
   * The inner reference callback.
   */
  readonly innerRef?: (id: string, ref: HTMLTableCellElement | null) => void;

  /**
   * Indicates whether the cell is transitioning to/from something to the left.
   */
  readonly transitionLeft?: boolean;

  /**
   * Indicates whether the cell is transitioning to/from something to the right.
   */
  readonly transitionRight?: boolean;

  /**
   * The sort direction.
   */
  readonly sortDirection?: SortDirection;

  /**
   * Called when the header is clicked.
   */
  readonly onClick?: (name: string, direction: SortDirection | null, exclusive: boolean) => void;

  /**
   * The colummn position.
   */
  readonly position?: number;
}

/**
 * A table column header.
 */
export const TableColumnHeader = (props: TableColumnHeaderProps) => {
  const {
    name,
    title,
    className,
    lock,
    position,
    transitionLeft = false,
    transitionRight = false,
    align = "left",
    sortable = false,
    sortDirection = null,
    innerRef,
    onClick,
  } = props;

  const { styles } = useTableStyles();
  const elementRef = useRef<HTMLTableCellElement | null>(null);

  const combinedClassName = classNames(
    styles.TableColumnHeader,
    "column-header",
    lock !== undefined ? `lock-${lock}` : undefined,
    {
      "transition-left": transitionLeft,
      "transition-right": transitionRight,
      sortable: !!sortable,
    },
    className,
  );

  const style = useMemo((): CSSProperties => {
    if (lock === "left") return { left: position };
    if (lock === "right") return { right: position };
    return {};
  }, [lock, position]);

  const clickHandler = useCallback(
    (ev: MouseEvent) => {
      const { button, shiftKey } = ev;

      if (button !== 0) return;

      let nextDirection: SortDirection | null = null;
      if (sortDirection === null) nextDirection = "ascending";
      else if (sortDirection === "ascending") nextDirection = "descending";
      else if (sortDirection === "descending") nextDirection = null;

      if (sortable) {
        if (typeof sortable === "string") {
          onClick?.(sortable, nextDirection, !shiftKey);
        } else {
          onClick?.(name, nextDirection, !shiftKey);
        }
      }

      ev.preventDefault();
    },
    [name, sortable, sortDirection, onClick],
  );

  const keyPressHandler = useCallback(
    (ev: KeyboardEvent) => {
      const { key, shiftKey } = ev;

      if (key === "Enter" || key === "Return") {
        let nextDirection: SortDirection = null;
        if (sortDirection === null) nextDirection = "ascending";
        else if (sortDirection === "ascending") nextDirection = "descending";
        else if (sortDirection === "descending") nextDirection = null;

        if (sortable) {
          if (typeof sortable === "string") {
            onClick?.(sortable, nextDirection, !shiftKey);
          } else {
            onClick?.(name, nextDirection, !shiftKey);
          }
        }

        ev.preventDefault();
      }
    },
    [name, sortable, sortDirection, onClick],
  );

  const refHandler = useCallback(
    (ref: HTMLTableCellElement | null) => {
      if (ref !== elementRef.current) {
        elementRef.current = ref;
        innerRef?.(name, ref);
      }
    },
    [name],
  );

  useEffect(() => {
    if (elementRef.current) {
      innerRef?.(name, elementRef.current);
    }

    return () => {
      innerRef?.(name, null);
    };
  }, [name]);

  const mouseDownHandler = useCallback((ev: MouseEvent) => {
    ev.preventDefault();
  }, []);

  const getAriaLabel = useCallback((): string | undefined => {
    if (!sortable) return undefined;

    const columnName = title || name;
    let sortState: string;
    let action: string;

    if (sortDirection === null) {
      sortState = "not sorted";
      action = "sort ascending";
    } else if (sortDirection === "ascending") {
      sortState = "sorted ascending";
      action = "sort descending";
    } else {
      sortState = "sorted descending";
      action = "remove sort";
    }

    return `${columnName}, ${sortState}. Press to ${action}`;
  }, [name, title, sortable, sortDirection]);

  return (
    <HeaderWrapper
      ref={refHandler}
      className={combinedClassName}
      style={style}
      tabIndex={sortable ? 0 : undefined}
      onClick={sortable ? clickHandler : undefined}
      onMouseDown={sortable ? mouseDownHandler : undefined}
      onKeyPress={sortable ? keyPressHandler : undefined}
      aria-sort={sortable && sortDirection ? sortDirection : undefined}
      aria-label={getAriaLabel()}
    >
      {lock === "left" && transitionRight && <div className="scroll-indicator left" />}
      {lock === "right" && transitionLeft && <div className="scroll-indicator right" />}
      <div className="cell-outer-content">
        <div className="cell-inner-content">
          {(!!title || sortable) && (
            <Shelf spacing="mini">
              {align === "right" && <Shelf.Spacer />}
              {!!title && <Semibold>{title}</Semibold>}
              {sortable && (
                <>
                  {sortDirection === "ascending" && (
                    <Icon className="sort-indicator" color={Color.Turqoise[500]} name="arrow-up" size="small" aria-hidden="true" />
                  )}
                  {sortDirection === "descending" && (
                    <Icon className="sort-indicator" color={Color.Turqoise[500]} name="arrow-down" size="small" aria-hidden="true" />
                  )}
                  {sortDirection === null && <Icon className="sort-indicator unsorted" name="arrow-down" size="small" aria-hidden="true" />}
                </>
              )}
              {align === "left" && <Shelf.Spacer />}
            </Shelf>
          )}
        </div>
      </div>
    </HeaderWrapper>
  );
};
